/**
 * Lightweight but complete markdown renderer for the AI Chat SDK.
 * Supports: bold, italic, inline code, code blocks, headers (h1-h3),
 * unordered & ordered lists, blockquotes, horizontal rules, and links.
 * All output is XSS-safe — HTML is escaped before inline rules are applied.
 */

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function applyInline(text: string): string {
  return (
    text
      // Bold+Italic: ***text***
      .replace(/\*\*\*(.*?)\*\*\*/g, "<strong><em>$1</em></strong>")
      // Bold: **text**
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      // Italic: *text* or _text_
      .replace(/\*(.*?)\*/g, "<em>$1</em>")
      .replace(/_(.*?)_/g, "<em>$1</em>")
      // Inline code: `code`
      .replace(/`([^`]+)`/g, '<code class="ac-code-inline">$1</code>')
      // Links: [label](url)
      .replace(
        /\[(.*?)\]\((https?:\/\/[^\s)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
      )
  );
}

export function formatMarkdown(raw: string): string {
  if (!raw) return "";

  const lines = raw.split("\n");
  let html = "";
  let i = 0;

  function consumeCodeBlock(): string {
    const fence = lines[i];
    const lang = fence.replace(/^```/, "").trim();
    i++;
    const codeLines: string[] = [];
    while (i < lines.length && !lines[i].startsWith("```")) {
      codeLines.push(escapeHtml(lines[i]));
      i++;
    }
    i++; // skip closing ```
    const langAttr = lang ? ` class="language-${escapeHtml(lang)}"` : "";
    return `<pre class="ac-code-block"><code${langAttr}>${codeLines.join("\n")}</code></pre>`;
  }

  function consumeBlockquote(): string {
    const inner: string[] = [];
    while (i < lines.length && lines[i].trimStart().startsWith(">")) {
      inner.push(lines[i].replace(/^\s*>\s?/, ""));
      i++;
    }
    return `<blockquote class="ac-blockquote">${applyInline(escapeHtml(inner.join(" ")))}</blockquote>`;
  }

  function consumeUl(): string {
    const items: string[] = [];
    while (i < lines.length && /^\s*[-*]\s/.test(lines[i])) {
      items.push(`<li>${applyInline(escapeHtml(lines[i].replace(/^\s*[-*]\s/, "")))}</li>`);
      i++;
    }
    return `<ul class="ac-list">${items.join("")}</ul>`;
  }

  function consumeOl(): string {
    const items: string[] = [];
    while (i < lines.length && /^\s*\d+\.\s/.test(lines[i])) {
      items.push(`<li>${applyInline(escapeHtml(lines[i].replace(/^\s*\d+\.\s/, "")))}</li>`);
      i++;
    }
    return `<ol class="ac-list ac-list-ol">${items.join("")}</ol>`;
  }

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith("```")) {
      html += consumeCodeBlock();
      continue;
    }

    if (line.trimStart().startsWith(">")) {
      html += consumeBlockquote();
      continue;
    }

    if (/^(\s*[-_*]){3,}\s*$/.test(line)) {
      html += `<hr class="ac-hr" />`;
      i++;
      continue;
    }

    const h3 = line.match(/^###\s+(.*)/);
    if (h3) { html += `<h3 class="ac-h3">${applyInline(escapeHtml(h3[1]))}</h3>`; i++; continue; }
    const h2 = line.match(/^##\s+(.*)/);
    if (h2) { html += `<h2 class="ac-h2">${applyInline(escapeHtml(h2[1]))}</h2>`; i++; continue; }
    const h1 = line.match(/^#\s+(.*)/);
    if (h1) { html += `<h1 class="ac-h1">${applyInline(escapeHtml(h1[1]))}</h1>`; i++; continue; }

    if (/^\s*[-*]\s/.test(line)) { html += consumeUl(); continue; }
    if (/^\s*\d+\.\s/.test(line)) { html += consumeOl(); continue; }

    if (line.trim() === "") {
      html += `<div class="ac-spacer"></div>`;
      i++;
      continue;
    }

    html += `<p class="ac-p">${applyInline(escapeHtml(line))}</p>`;
    i++;
  }

  return html;
}
